<template>
  <div class="user-contact" :class="{ 'app-invalid-group': !isComponentValid }">
    <h4>Confirmation will be sent via email and / or text for the transaction(s) in the cart</h4>
    <fieldset>
      <legend>
        <div class="component-container">
          <ContactEmail
            :verify="verify"
            @doEmail="setEmail"
            @doResetVerify="resetVerify"
            :embedded="true"
            ref="contactEmailRef"
          />
          <p hidden ref="noContactInfo" class="app-invalid-msg msg-offset-1">
            Email is required.
          </p>
          <ContactPhone
            :verify="verify"
            :show-phone="showPhone"
            @doPhone="setPhone"
            @doResetVerify="resetVerify"
            :embedded="true"
            ref="contactPhoneRef"
          />    
        </div>
      </legend>
    </fieldset>
  </div>
</template>


<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import * as COMMONS from '../../store/_commons';
import ContactPhone from '@/components/contact/ContactPhone';
import ContactEmail from '../../components/contact/ContactEmail.vue';
import { usePhoneFormatter } from '@/composables/usePhoneFormatter';

const props = defineProps({
  verify: {
    type: Boolean,
    required: true,
  },
  showPhone: {
    type: Boolean,
    default: false,
  }
});

// Emits
const emit = defineEmits(['doContactValidated', 'doResetVerify']);

// Vuex store
const store = useStore();

// Reactive State
const myPhone = ref('');
const myEmail = ref('');
const myNotification = ref(true);

const isComponentValid = ref(true);
const isModify = ref(false);

const isPhoneValid = ref(true); 
const isEmailValid = ref(false); 

// Refs for child components
const contactPhoneRef = ref(null);
const contactEmailRef = ref(null);

const noContactInfo = ref(null);

// Composables 
const { formatPhoneForWebapp } = usePhoneFormatter();

// Computed properties
const isBusinessUser = computed(() => store.state.guestLogin.isBusinessUser);

const personalInfo = computed(() => {
  if (isBusinessUser.value) {
    return store.getters[COMMONS.BUSINESS_PROFILE_INFO];
  }
  return store.getters[COMMONS.GUEST_PERSONAL_INFO];
});

// Watchers
watch(() => props.verify, async (newValue) => {
  if (newValue) {
    // Validate both when verify flag is set
    if (contactEmailRef.value) {
      await contactEmailRef.value.checkEmail();
    }
    if (contactPhoneRef.value && contactPhoneRef.value.myPhone?.length > 0) {
      await contactPhoneRef.value.checkPhone();
    }
  }
});

// Methods
/**
 *  Email required, phone optional but must be valid if provided
 * @returns {boolean} - True if the contact information is valid, false otherwise.
 */
const continueToNextStep = async () => {
  resetVerify(); 
  
  // Validate Email
  if (!contactEmailRef.value) {
    return false;
  }

  await contactEmailRef.value.checkEmail();
  
  const hasEmail = myEmail.value.length > 0;
  const emailHasInvalidChars = contactEmailRef.value.invalidEmailChars;
  const emailIsValid = hasEmail && !emailHasInvalidChars && isEmailValid.value;
  
  if (!hasEmail) {
    isComponentValid.value = false;
    handleHidden({ ref: 'noContactInfo', error: true });
    return false;
  }
  
  if (!emailIsValid) {
    isComponentValid.value = false;
    return false;
  }

  // Validate Phone
  const hasPhone = !!myPhone.value || contactPhoneRef.value.invalidPhone || contactPhoneRef.value.invalidPhoneNumChars;

  if (hasPhone) {
    
    const phoneIsValid = await contactPhoneRef.value.checkPhone();
    
    if (!phoneIsValid) {
      isComponentValid.value = false;
      isPhoneValid.value = false;
      return false;
    }
    
    isPhoneValid.value = true;
  } else {
    // No phone provided, which is valid since phone is optional
    isPhoneValid.value = true;
  }

  // Hide "Email is required" message if it was showing
  handleHidden({ ref: 'noContactInfo', error: false });
  
  // All validations passed
  isComponentValid.value = true;
  isEmailValid.value = true;

  return isComponentValid.value;
};

// Method to expose the component's data to the parent
const getContactData = () => {
  return {
    phone: myPhone.value,
    email: myEmail.value,
    isConsentProvided: myNotification.value,
    isModifyRequest: isModify.value,
  };
};

const getIsConsentProvided = () => {
  if (store.state.guestLogin.finalizationCart) {
    const userContact = store.state.guestLogin.finalizationCart;
    if (userContact && userContact.isConsentProvided !==  undefined) {
      return (userContact.isConsentProvided);
    }
  }

  return undefined;
};

const handleHidden = (data) => {
  const attr = 'hidden';
  const errorElem = noContactInfo.value;  
  const hasError = data.error;
  if (errorElem) {
    if (hasError) {
      errorElem.removeAttribute(attr);
    } else {
      errorElem.setAttribute(attr, attr);
    }
  }
};

const hasContactInfo = () => {
  return myEmail.value.length > 0;
};

const resetVerify = () => {
  // Reset children validations
  isPhoneValid.value = true; // Phone is optional, default to valid
  isEmailValid.value = false; // Email is required, default to invalid

  // Ask parent to reset its flag
  emit('doResetVerify');
};

/**
 * Work differently from ModalContact's setEmail due to differences in props behavior.
 * @param data
 */
const setEmail = (data) => {
  myEmail.value = data.email;

  // Validate from child component if available
  if (contactEmailRef.value) {
    const hasInvalidChars = contactEmailRef.value.invalidEmailChars;
    isEmailValid.value = data.isValid && !hasInvalidChars;
  } else {
    isEmailValid.value = data.isValid;
  }
};

/**
 * Track phone validity from child component
 * Work differently from ModalContact's setPhone due to differences in props behavior.
 * @param data
 */
const setPhone = async (data) => {
  myPhone.value = data.phone;
  
  // Validate from child component if phone is provided
  if (contactPhoneRef.value ) {
    // Read current validation state from child
    const phoneInvalid = contactPhoneRef.value.invalidPhone;
    const hasInvalidChars = contactPhoneRef.value.invalidPhoneNumChars;
    
    // Phone is valid only if: not invalid AND no invalid chars
    isPhoneValid.value = !(phoneInvalid || hasInvalidChars);
  } else {
    // No phone provided is valid (phone is optional)
    isPhoneValid.value = data.isValid; // Should be true if no phone, or valid if phone provided
  }
};

// eslint-disable-next-line no-unused-vars
const setNotifyMe = (e) => {
  myNotification.value = e.target.value;
};

const toggleRequirementError = () => {
  // Validate email
  if (contactEmailRef.value) {
    contactEmailRef.value.checkEmail();
    const hasInvalidChars = contactEmailRef.value.invalidEmailChars;
    isEmailValid.value = myEmail.value.length > 0 && !hasInvalidChars;
  }
  
  // Validate phone if provided
  if (contactPhoneRef.value && contactPhoneRef.value.myPhone?.length > 0) {
    contactPhoneRef.value.checkPhone();
    
    const phoneInvalid = contactPhoneRef.value.invalidPhone;
    const hasInvalidChars = contactPhoneRef.value.invalidPhoneNumChars;
    
    isPhoneValid.value = !phoneInvalid && !hasInvalidChars;
  }
  
  // Show "Email is required" error if no email
  const hasContact = hasContactInfo();
  if (!hasContact) {
    handleHidden({ ref: 'noContactInfo', error: true });
  }
};

const populateContactFields = (email, phone) => {
  if (contactEmailRef.value && email) {
    contactEmailRef.value.myEmail = email;
    myEmail.value = email;
  }
  const phoneNum = formatPhoneForWebapp(phone);
  if (phoneNum?.length === 10 && contactPhoneRef.value) {
    contactPhoneRef.value.split(phoneNum);
    myPhone.value = phoneNum;
  }
};

// Lifecycle Hooks
onMounted(() => {
  const info = isBusinessUser.value
    ? personalInfo.value?.businessPersonalInformation
    : personalInfo.value?.personalInformation;

  if (info) {
    const email = isBusinessUser.value ? info.officeEmail : info.email;
    const phone = isBusinessUser.value ? info.cellPhone : info.phone;
    populateContactFields(email, phone);
  }

  myNotification.value = getIsConsentProvided();
});

defineExpose({
  continueToNextStep,
  hasContactInfo,
  toggleRequirementError,
  myPhone,
  myEmail,
  getContactData
});
</script>


<style lang="scss" scoped>
.user-contact {
  border: 1px solid var(--ri-gray);
  margin: 0.5em 0 1.5em;
  margin-top: 0rem;
  padding: calc(var(--padding-horizontal) * 0.125) calc(var(--padding-horizontal) * 0.35);

  h4{
    margin-top: 0.5rem;
  }
  
  @media only screen and (min-width: 330px) and (max-width: 405px) {
      padding: 1.1rem;
    }
    
  .component-container {
    display: block;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: flex-start;
    .conjunction {
      // do not use flex alignment due to conditional error <p>
      line-height: 2em;
      padding: 0 0.75em;
      margin-top: 0;
      @media (min-width: 355px) {
        margin-top: 2.5em;
      }
    }
  }
  &.app-invalid-group {     // does not override main's app-invalid-group but as a scrollIntoView indicator
    border: 1px solid var(--ri-red);
    box-shadow: 0 0 2px 2px var(--ri-red);
  }
  input[type="radio"] {
    margin: 0.5em 0;
  }
  .msg-offset {
    &-1 {
      margin-top: -1.25em;
      margin-bottom: 1em;
    }
    &-2 {
      margin-top: -1em;
      margin-bottom: 0.5em;
    }
  }
}
</style>
